/**
 * Typed client for the server API (TD §9). One function per route; every failure is thrown as an
 * `ApiFailure` with a friendly, plain-English `message` ready to show to the user.
 *
 * A 401 from any route (except sign-in itself) calls the handler set with `setUnauthorizedHandler`,
 * which the session store uses to send the user back to the sign-in page.
 */
import type {
  ApiError,
  ApiErrorCode,
  CreatePlanRequest,
  CreateVersionRequest,
  GetPlanResponse,
  GetVersionResponse,
  LoginRequest,
  MeResponse,
  PlanDoc,
  PlanId,
  PlanMeta,
  PresenceResponse,
  RestoreVersionResponse,
  SavePlanResponse,
  SpeciesId,
  SpeciesOverrideField,
  SpeciesWithOverrides,
  TrashedPlanMeta,
  VersionId,
  VersionMeta,
} from '../shared/types';

/** Server error codes plus `offline`, used when the server can't be reached at all. */
export type FailureCode = ApiErrorCode | 'offline';

/** A failed API call. `message` is safe to show to the user as is. */
export class ApiFailure extends Error {
  constructor(
    public readonly code: FailureCode,
    message: string,
    /** HTTP status, or 0 when the server couldn't be reached. */
    public readonly status: number,
    /** Seconds to wait, for `rate_limited`. */
    public readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }

  /** True when trying again later may work (offline, server hiccup). */
  get retryable(): boolean {
    return this.code === 'offline' || this.code === 'server_error' || this.status >= 500 || this.status === 408;
  }
}

/** "5 minutes", "1 minute", "30 seconds": a wait time in plain English. */
export function formatWait(seconds: number): string {
  const s = Math.max(1, Math.ceil(seconds));
  if (s < 60) return s === 1 ? '1 second' : `${s} seconds`;
  const m = Math.ceil(s / 60);
  return m === 1 ? '1 minute' : `${m} minutes`;
}

/**
 * The friendly message for an error code. The server's own message is used where it is already
 * written for people (bad_request, too_large); everything else gets a fixed sentence.
 */
export function friendlyMessage(code: FailureCode, serverMessage?: string, retryAfter?: number): string {
  switch (code) {
    case 'offline':
      return "Can't reach the planner. Check your connection.";
    case 'unauthorized':
      return 'Please sign in again.';
    case 'rate_limited':
      return retryAfter !== undefined
        ? `Too many tries. Please wait ${formatWait(retryAfter)} and try again.`
        : 'Too many tries. Please wait a few minutes and try again.';
    case 'not_found':
      return "That doesn't exist any more. It may have been deleted.";
    case 'invalid_plan':
      return "Something in this plan didn't look right, so it wasn't saved. Please try again or undo your last change.";
    case 'too_large':
      return serverMessage && /limit/i.test(serverMessage) ? serverMessage : 'That is too large.';
    case 'bad_request':
      return serverMessage || "That didn't work. Please check what you typed.";
    case 'server_error':
    default:
      return 'Something went wrong on the server. Please try again.';
  }
}

let onUnauthorized: (() => void) | null = null;

/** Sets what happens when any call gets a 401 (the session store sends the user to sign-in). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

const CODES: ReadonlySet<string> = new Set<ApiErrorCode>([
  'unauthorized', 'rate_limited', 'not_found', 'invalid_plan', 'too_large', 'bad_request', 'server_error',
]);

function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 429) return 'rate_limited';
  if (status === 404) return 'not_found';
  if (status === 413) return 'too_large';
  if (status >= 400 && status < 500) return 'bad_request';
  return 'server_error';
}

/** Turns a non-ok response into an ApiFailure, reading the `{error}` body when there is one. */
async function failureFrom(res: Response): Promise<ApiFailure> {
  let code: ApiErrorCode = codeForStatus(res.status);
  let message: string | undefined;
  let retryAfter: number | undefined;
  try {
    const body = (await res.json()) as Partial<ApiError>;
    if (body.error && CODES.has(body.error.code)) {
      code = body.error.code;
      message = body.error.message;
      retryAfter = body.error.retryAfter;
    }
  } catch {
    // Not JSON (a proxy error page, say): keep the status-based code.
  }
  if (retryAfter === undefined && code === 'rate_limited') {
    const header = Number(res.headers.get('Retry-After'));
    if (Number.isFinite(header) && header > 0) retryAfter = header;
  }
  // A proxy in front of a stopped server answers 502/503/504: treat that like being offline.
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return new ApiFailure('offline', friendlyMessage('offline'), res.status);
  }
  return new ApiFailure(code, friendlyMessage(code, message, retryAfter), res.status, retryAfter);
}

interface RequestOptions {
  method?: string;
  /** JSON body; a Blob is sent raw with its own type. */
  body?: unknown;
  keepalive?: boolean;
  /** Don't call the 401 handler (sign-in uses this: a wrong password is a 401 too). */
  quiet401?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = { method: opts.method ?? 'GET', credentials: 'same-origin', keepalive: opts.keepalive ?? false };
  if (opts.body instanceof Blob) {
    init.body = opts.body;
    init.headers = { 'Content-Type': opts.body.type || 'application/octet-stream' };
  } else if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    init.headers = { 'Content-Type': 'application/json' };
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiFailure('offline', friendlyMessage('offline'), 0);
  }
  if (!res.ok) {
    const failure = await failureFrom(res);
    if (failure.code === 'unauthorized' && !opts.quiet401) onUnauthorized?.();
    throw failure;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const enc = encodeURIComponent;

// ---- Sign-in ---------------------------------------------------------------------------------

/** Signs in with the site password and the person's name; sets the session cookie. */
export function login(req: LoginRequest): Promise<void> {
  return request('/api/login', { method: 'POST', body: req, quiet401: true });
}

/** Signs out (clears the cookie). */
export function logout(): Promise<void> {
  return request('/api/logout', { method: 'POST', body: {}, quiet401: true });
}

/** Who is signed in. Throws `unauthorized` when nobody is (without calling the 401 handler). */
export function me(): Promise<MeResponse> {
  return request('/api/me', { quiet401: true });
}

// ---- Plans -----------------------------------------------------------------------------------

/** All plans, most recently edited first. */
export function listPlans(): Promise<PlanMeta[]> {
  return request('/api/plans');
}

/** Creates a plan and returns its meta (navigate to `/plan/<id>` next). */
export function createPlan(req: CreatePlanRequest): Promise<PlanMeta> {
  return request('/api/plans', { method: 'POST', body: req });
}

/** One plan with its document. */
export function getPlan(id: PlanId): Promise<GetPlanResponse> {
  return request(`/api/plans/${enc(id)}`);
}

/**
 * Saves the document. `baseRev` is the rev this edit started from; when someone else saved in
 * between, the server keeps their state as a version and answers `overwrote: true`.
 * `keepalive` lets the request finish while the page closes (bodies up to 64 KB only).
 */
export function savePlan(id: PlanId, doc: PlanDoc, baseRev: number, keepalive = false): Promise<SavePlanResponse> {
  return request(`/api/plans/${enc(id)}`, { method: 'PUT', body: { doc, baseRev }, keepalive });
}

/** Renames a plan (the name lives in the plan's meta; the document follows it). */
export function renamePlan(id: PlanId, name: string): Promise<PlanMeta> {
  return request(`/api/plans/${enc(id)}`, { method: 'PATCH', body: { name } });
}

/** Copies a plan as "<name> (copy)" (without its history). */
export function duplicatePlan(id: PlanId): Promise<PlanMeta> {
  return request(`/api/plans/${enc(id)}/duplicate`, { method: 'POST', body: {} });
}

/** Moves a plan to the Deleted section (kept for 30 days). */
export function deletePlan(id: PlanId): Promise<void> {
  return request(`/api/plans/${enc(id)}`, { method: 'DELETE' });
}

/** Deleted plans, most recently deleted first. */
export function listTrash(): Promise<TrashedPlanMeta[]> {
  return request('/api/trash');
}

/** Brings a deleted plan back. */
export function restoreFromTrash(id: PlanId): Promise<PlanMeta> {
  return request(`/api/trash/${enc(id)}/restore`, { method: 'POST', body: {} });
}

// ---- Versions --------------------------------------------------------------------------------

/** A plan's saved versions, newest first. */
export function listVersions(id: PlanId): Promise<VersionMeta[]> {
  return request(`/api/plans/${enc(id)}/versions`);
}

/** Keeps the server's current state of the plan as a version (save first). */
export function createVersion(id: PlanId, req: CreateVersionRequest = {}, keepalive = false): Promise<VersionMeta> {
  return request(`/api/plans/${enc(id)}/versions`, { method: 'POST', body: req, keepalive });
}

/** One version with its document, for previewing. */
export function getVersion(id: PlanId, vid: VersionId): Promise<GetVersionResponse> {
  return request(`/api/plans/${enc(id)}/versions/${enc(vid)}`);
}

/** Makes a version the current plan (the current state is kept as "Before restore" first). */
export function restoreVersion(id: PlanId, vid: VersionId): Promise<RestoreVersionResponse> {
  return request(`/api/plans/${enc(id)}/versions/${enc(vid)}/restore`, { method: 'POST', body: {} });
}

// ---- Images ----------------------------------------------------------------------------------

/** Uploads the plan's card picture (PNG, at most 200 KB). */
export function uploadThumb(id: PlanId, png: Blob): Promise<void> {
  return request(`/api/plans/${enc(id)}/thumb`, { method: 'PUT', body: png.type ? png : new Blob([png], { type: 'image/png' }) });
}

/**
 * Uploads the screenshot overlay (PNG or JPEG, at most 4 MB; downscale to ≤ 2048 px first).
 * Returns the stored file name to put in `doc.overlay.file`.
 */
export function uploadOverlay(id: PlanId, image: Blob): Promise<{ file: string }> {
  return request(`/api/plans/${enc(id)}/overlay`, { method: 'PUT', body: image });
}

/** URL of the plan's card picture. Pass the rev so a new picture isn't hidden by the browser cache. */
export function thumbUrl(id: PlanId, rev?: number): string {
  return `/files/plans/${enc(id)}/thumb.png${rev === undefined ? '' : `?r=${rev}`}`;
}

/** URL of the plan's screenshot overlay. */
export function overlayUrl(id: PlanId, cacheKey?: string | number): string {
  return `/files/plans/${enc(id)}/overlay${cacheKey === undefined ? '' : `?r=${enc(String(cacheKey))}`}`;
}

// ---- Species ---------------------------------------------------------------------------------

/** Built-in species with the shared corrections applied. */
export function listSpecies(): Promise<SpeciesWithOverrides[]> {
  return request('/api/species');
}

/** Sets a shared correction for one species value. */
export function setOverride(id: SpeciesId, field: SpeciesOverrideField, value: unknown): Promise<SpeciesWithOverrides> {
  return request(`/api/species/${enc(id)}/override`, { method: 'PUT', body: { field, value } });
}

/** Resets one species value to the built-in one. */
export function clearOverride(id: SpeciesId, field: SpeciesOverrideField): Promise<SpeciesWithOverrides> {
  return request(`/api/species/${enc(id)}/override/${enc(field)}`, { method: 'DELETE' });
}

// ---- Presence --------------------------------------------------------------------------------

/** Heartbeat for an open plan; returns the names of the others who have it open. */
export function presence(planId: PlanId): Promise<PresenceResponse> {
  return request(`/api/presence/${enc(planId)}`, { method: 'POST', body: {} });
}
